import React, { useEffect, Fragment, useMemo } from "react";
import { Field, useForm } from "react-final-form";
import classnames from "classnames";
import { composeValidators, is_empty } from "utils/validations";
import useStyles from "./styles";
import { checkIsArrayEmpty } from "@my-scoot/exly-react-component-lib";
import {
  ExlyDropdown,
  ExlyInput,
  ExlyOfferingSelect,
  ExlyTextarea,
} from "common/form";
import {
  code_postion_options,
  custom_code_form_field_names,
  custom_code_target,
  target_dropdown_options,
} from "../../Codes.config";
import { isCustomCodeAllowed } from "common/validate";
import ExlyComplexDropdown from "common/form/ExlyComplexDropdown";
import { useFetchCustomCodeOfferings } from "../../hooks/useFetchCustomCodeOfferings";
import { useFetchCustomCodeSalesPgs } from "../../hooks/useFetchCustomCodeSalesPgs";
import { useFetchCustomCodeTyPgs } from "../../hooks/useFetchCustomCodeTyPgs";
import SalesPageOption from "../HelperComponents/SalesPageOption";
import EmptySalesPageView from "../HelperComponents/EmptySalesPageView";
import OfferingSpecificPageOption from "../HelperComponents/OfferingSpecificPageOption";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { logError } from "utils/error";
import CustomCodeSelect from "../HelperComponents/CustomCodeSelect";
import {
  getApplicablePagesTitleLabel,
  getFilteredSalesPgsByListings,
  getFilteredThankyouPgs,
  getPagesOptionsAsPerPageType,
  handleListingWisePgsSelector,
  handleShowCombosPgsSelector,
  handleShowOfferingSelector,
  handleShowSalesPgsSelector,
  handleShowSpecificPagesSelector,
} from "./utils";
import { ComboPageOption } from "../HelperComponents/CombosPageOption";
import { useFetchCustomCodeComboPage } from "../../hooks/useFetchCustomCodeComboPage";
import { fetchListingBasedThankyouPgs } from "../../Codes.api.helpers";
import { schedule_types_ids } from "features/Listings/constants/Listings.constants";
import { useFetchCustomCodeBrandedOfferings } from "../../hooks/useFetchCustomCodeBrandedOfferings";
import { useFetchCustomCodeRCOfferings } from "../../hooks/useFetchCustomCodeRCOfferings";

export const required = (value) => {
  if (is_empty(value)) return "Required";
};

const AddCodeFormFields = ({ values, submitFailed, hasValidationError }) => {
  const classes = useStyles();
  const form = useForm();
  const { notify } = useNotifications();

  const {
    target,
    applicable_pages,
    metadata: {
      listings,
      combo_pages: comboPages,
      all_content_pages = false,
      all_branded_pages = false,
    },
  } = values;

  const isComboPageSelected = target == custom_code_target.COMBO_PAGE;

  const showOfferingSelector = useMemo(
    () => handleShowOfferingSelector(target),
    [target]
  );

  const showSpecificPagesSelector = useMemo(
    () => handleShowSpecificPagesSelector({ listings, comboPages, target }),
    [listings, comboPages, target]
  );

  const showSalesPagesSelector = useMemo(
    () => handleShowSalesPgsSelector(applicable_pages),
    [applicable_pages]
  );

  const showRCPagesSelector = useMemo(
    () =>
      handleListingWisePgsSelector(
        applicable_pages,
        schedule_types_ids.content
      ),
    [applicable_pages]
  );

  const showBrandedCommunityPagesSelector = useMemo(
    () =>
      handleListingWisePgsSelector(
        applicable_pages,
        schedule_types_ids.branded_community
      ),
    [applicable_pages]
  );

  const showCombosPagesSelector = useMemo(
    () => handleShowCombosPgsSelector(target),
    [target]
  );

  const { loading, offeringOptions, lastElementRef } =
    useFetchCustomCodeOfferings({ fetch: showOfferingSelector });
  const {
    loading: brandedLoading,
    offeringOptions: brandedOfferingOptions,
    lastElementRef: brandedLastElementRef,
  } = useFetchCustomCodeBrandedOfferings({
    fetch: showBrandedCommunityPagesSelector,
    initialData: values,
  });

  const {
    loading: rcLoading,
    offeringOptions: rcOfferingOptions,
    lastElementRef: rcLastElementRef,
  } = useFetchCustomCodeRCOfferings({
    fetch: showRCPagesSelector,
    initialData: values,
  });

  const { loading: salesPgOptionsLoading, salesPgOptions } =
    useFetchCustomCodeSalesPgs({ listings });
  const { thankyouPgList } = useFetchCustomCodeTyPgs({
    listings,
    isComboPageSelected,
  });

  const {
    data: comboPagesOptions,
    lastElementRef: lastElementComboPagesRef,
    loading: comboPagesLoader,
  } = useFetchCustomCodeComboPage({ initialData: values });

  useEffect(() => {
    if (submitFailed || hasValidationError)
      notify("Invalid Values", notification_color_keys.error);
  }, [submitFailed, hasValidationError]);

  useEffect(() => {
    if (isComboPageSelected) return;
    const filteredThankyouPgs = getFilteredThankyouPgs(
      applicable_pages,
      thankyouPgList
    );
    form.change(
      custom_code_form_field_names.thankyou_pages,
      filteredThankyouPgs
    );
  }, [thankyouPgList, applicable_pages]);

  const handleTargetChange = (value) => {
    form.resetFieldState(custom_code_form_field_names.listings);
    form.resetFieldState(custom_code_form_field_names.applicable_pages);
    form.resetFieldState(custom_code_form_field_names.sales_pages);
    form.resetFieldState(custom_code_form_field_names.combo_pages);
    form.resetFieldState(custom_code_form_field_names.branded_pages);
    form.resetFieldState(custom_code_form_field_names.content_pages);
    if (
      value === custom_code_target.HOME ||
      value === custom_code_target.ALL_PAGES_OF_ALL_OFFERINGS ||
      value === custom_code_target.COMBO_PAGE ||
      value === custom_code_target.EUD ||
      value === custom_code_target.SPECIFIC_OFFERING_PAGES
    ) {
      form.change(custom_code_form_field_names.combo_pages, []);
      form.change(custom_code_form_field_names.applicable_pages, []);
      form.change(custom_code_form_field_names.listings, []);
      form.change(custom_code_form_field_names.sales_pages, []);
      form.change(custom_code_form_field_names.thankyou_pages, []);
      form.change(custom_code_form_field_names.branded_pages, []);
      form.change(custom_code_form_field_names.content_pages, []);
    }
    if (value === custom_code_target.EUD) {
      const defauktAllOptionIds = getPagesOptionsAsPerPageType(value).map(
        (option) => option.id
      );
      form.change(
        custom_code_form_field_names.applicable_pages,
        defauktAllOptionIds
      );
    }
  };

  const handleOfferingChange = (values) => {
    const filteredSalesPages = getFilteredSalesPgsByListings(
      values,
      salesPgOptions
    );
    form.change(custom_code_form_field_names.sales_pages, filteredSalesPages);
  };

  const handleOnChangeCombo = (applicablePages) => {
    form.change(custom_code_form_field_names.applicable_pages, applicablePages);
    if (checkIsArrayEmpty(applicablePages)) return;
    if (checkIsArrayEmpty(comboPages)) return;

    fetchListingBasedThankyouPgs({
      combos: comboPages.join(","),
    })
      .then(({ thankyou_pages }) => {
        const filteredThankyouPgs = getFilteredThankyouPgs(
          applicablePages,
          thankyou_pages
        );
        form.change(
          custom_code_form_field_names.thankyou_pages,
          filteredThankyouPgs
        );
      })
      .catch((error) => {
        logError({
          error,
          occuredAt:
            "src/ui/pages/CustomCodes/components/AddCodeModal/AddCodeFormFields.jsx",
          when: "fetchListingBasedThankyouPgs",
        });
      });
  };

  return (
    <div className={classes.contentWrap}>
      <Field
        name={custom_code_form_field_names.code_snippet}
        component={ExlyTextarea}
        label="Paste the custom code snippet here"
        placeholder="Enter the custom code snippet here"
        validate={composeValidators(required, isCustomCodeAllowed)}
        minWidth="100%"
        maxWidth="100%"
        customClasses={{ wrapper: classes.mb }}
      />
      <Field
        name={custom_code_form_field_names.name}
        label="Custom code name"
        validate={required}
        size="small"
        component={ExlyInput}
        fullWidth
        placeholder="Enter custom code name"
        wrapperClassName={classes.mb}
      />

      <div className={classnames(classes.mb, classes.pageSelectWrap)}>
        <div className={classnames(classes.heading, "mb-3")}>Add Code to</div>
        <Field
          name={custom_code_form_field_names.target}
          component={ExlyDropdown}
          fullWidth
          size="small"
          options={target_dropdown_options}
          placeholder="Select pages"
          formControlClassName="mt-1"
          validate={required}
          dropdownLabel={{
            label: "Select pages",
            size: "small",
            weight: "semi-bold",
          }}
          onChange={handleTargetChange}
        />
        <Field
          name={custom_code_form_field_names.listings}
          validate={(value, allValues) => {
            const { target } = allValues;
            const showOfferingSelector = handleShowOfferingSelector(target);
            if (showOfferingSelector && is_empty(value)) return "Required";
          }}
          component={showOfferingSelector ? ExlyOfferingSelect : Fragment}
          label="Select Offerings"
          multiple
          mobileModalTitle="Select Offerings"
          placeholder="Select Offerings"
          selectAllContent="Choose all from the list"
          deselectAllContent="Remove all from the list"
          options={offeringOptions}
          loading={loading}
          showStatus={true}
          onChange={handleOfferingChange}
          lastElementRef={lastElementRef}
          classes={{ wrapper: classes.mt }}
        />

        {/* Show Combo packages */}
        <Field
          name={custom_code_form_field_names.combo_pages}
          component={showCombosPagesSelector ? CustomCodeSelect : Fragment}
          fullWidth
          size="small"
          options={comboPagesOptions}
          loading={comboPagesLoader}
          showStatus={true}
          label="Select Combo package(s)"
          mobileModalTitle="Select Combo package(s)"
          placeholder="Select Combo packages"
          multiple
          showChips
          showChipsCrossIcon
          renderOption={(renderProps) => (
            <ComboPageOption
              showCheckbox
              showStatus
              lastElementRef={lastElementComboPagesRef}
              {...renderProps}
            />
          )}
          headerComponent={
            is_empty(comboPagesOptions) &&
            !comboPagesLoader && <EmptySalesPageView />
          }
          validate={showCombosPagesSelector && required}
          classes={{ wrapper: classes.mt }}
        />

        <Field
          name={custom_code_form_field_names.applicable_pages}
          component={showSpecificPagesSelector ? CustomCodeSelect : Fragment}
          multiple
          options={getPagesOptionsAsPerPageType(target)}
          label={getApplicablePagesTitleLabel(target)}
          mobileModalTitle={getApplicablePagesTitleLabel(target)}
          placeholder={getApplicablePagesTitleLabel(target)}
          renderOption={OfferingSpecificPageOption}
          validate={(value) => {
            if (showSpecificPagesSelector && is_empty(value)) return "Required";
          }}
          classes={{ wrapper: classes.mt }}
          onChange={handleOnChangeCombo}
        />

        <Field
          name={custom_code_form_field_names.branded_pages}
          validate={(value) => {
            if (showBrandedCommunityPagesSelector && checkIsArrayEmpty(value)) {
              return "Required";
            }
          }}
          component={
            showBrandedCommunityPagesSelector ? ExlyOfferingSelect : Fragment
          }
          label="Select Branded Community Offerings"
          multiple
          showSelectAll
          mobileModalTitle="Select Branded Community Offerings"
          placeholder="Select Branded Community Offerings"
          selectAllContent="Choose all from the list"
          deselectAllContent="Remove all from the list"
          options={brandedOfferingOptions}
          loading={brandedLoading}
          showStatus={true}
          onChange={handleOfferingChange}
          lastElementRef={brandedLastElementRef}
          classes={{ wrapper: classes.mt }}
          selectAllState={all_branded_pages}
          onSelectAllChange={({ checked }) => {
            form.change(
              custom_code_form_field_names.all_branded_pages,
              checked
            );
          }}
          allOptionsSelectedLabel="All Offerings"
        />

        <Field
          name={custom_code_form_field_names.content_pages}
          validate={(value) => {
            if (showRCPagesSelector && checkIsArrayEmpty(value)) {
              return "Required";
            }
          }}
          component={showRCPagesSelector ? ExlyOfferingSelect : Fragment}
          label="Select Recorded Content Offerings"
          multiple
          showSelectAll
          mobileModalTitle="Select Recorded Content Offerings"
          placeholder="Select Recorded Content Offerings"
          selectAllContent="Choose all from the list"
          deselectAllContent="Remove all from the list"
          options={rcOfferingOptions}
          loading={rcLoading}
          showStatus={true}
          onChange={handleOfferingChange}
          lastElementRef={rcLastElementRef}
          classes={{ wrapper: classes.mt }}
          selectAllState={all_content_pages}
          onSelectAllChange={({ checked }) => {
            form.change(
              custom_code_form_field_names.all_content_pages,
              checked
            );
          }}
          allOptionsSelectedLabel="All Offerings"
        />

        <Field
          name={custom_code_form_field_names.sales_pages}
          component={
            showSalesPagesSelector && showOfferingSelector
              ? CustomCodeSelect
              : Fragment
          }
          fullWidth
          size="small"
          options={salesPgOptions}
          loading={salesPgOptionsLoading}
          showStatus={true}
          label="Select landing page(s)"
          mobileModalTitle="Select landing page(s)"
          placeholder="Select landing pages"
          multiple
          showChips
          showChipsCrossIcon
          renderOption={(renderProps) => (
            <SalesPageOption
              showCheckbox
              showStatus
              lastElementRef={lastElementRef}
              {...renderProps}
            />
          )}
          headerComponent={
            is_empty(salesPgOptions) &&
            !salesPgOptionsLoading && <EmptySalesPageView />
          }
          validate={showSalesPagesSelector && required}
          classes={{ wrapper: classes.mt }}
        />
      </div>

      <Field
        name={custom_code_form_field_names.code_position}
        component={ExlyComplexDropdown}
        fullWidth
        size="small"
        options={code_postion_options}
        placeholder="Place code in"
        checked
        validate={required}
        helperTextClassName={classes.errorTextWrap}
        dropdownLabel={{
          label: "Place code in",
          size: "small",
          weight: "semi-bold",
        }}
      />

      <div className={classes.alertWrap}>
        <span>
          <span className={classnames(classes.yelloish, "mr-2")}>Note*</span>-
          Adding third party code to your website’s head or body may affect its
          performance
        </span>
      </div>
    </div>
  );
};

export default AddCodeFormFields;
